import React from 'react';

/**
 * Wordmark — world-class bespoke lockup for The Tech Reach.
 *
 * Principles:
 *  - ONE colour, ONE voice — solid white with a barely-there gradient
 *    that only adds depth (never reads as two-tone).
 *  - Display face (Space Grotesk 700) with negative optical tracking
 *    on the main lockup for that engineered, editorial feel.
 *  - "THE" set as a small, wide-tracked eyebrow above so the eye lands
 *    on the real word: TECH REACH.
 *  - A tight, precise glyph-like separator between TECH and REACH,
 *    echoing the brandmark's geometry (not a random dot).
 *  - A fine cyan hairline runs across the bottom of the entire lockup,
 *    echoing the signal-arc in the brandmark — this is the ONE cyan
 *    accent, applied uniformly.
 */

const SIZE_MAP = {
  nav:  { eyebrow: 9,  main: 17,  tag: 8,  gap: 8,  bar: 1 },
  sm:   { eyebrow: 10, main: 26,  tag: 9,  gap: 12, bar: 1 },
  md:   { eyebrow: 12, main: 42,  tag: 11, gap: 20, bar: 2 },
  hero: { eyebrow: 14, main: 72,  tag: 12, gap: 32, bar: 2 },
};

const Wordmark = ({ size = 'nav', showTagline = true, className = '' }) => {
  const s = SIZE_MAP[size] || SIZE_MAP.nav;
  const isNav = size === 'nav';

  // Space between TECH and REACH (thin space, no dot)
  const sep = (
    <span
      aria-hidden="true"
      className="inline-block align-middle"
      style={{
        width: isNav ? 1 : 1.5,
        height: `${s.main * 0.7}px`,
        background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.35) 30%, rgba(255,255,255,0.35) 70%, transparent 100%)',
        margin: isNav ? '0 8px' : '0 14px',
        borderRadius: 1,
      }}
    />
  );

  return (
    <div className={`inline-flex flex-col leading-none select-none ${className}`}>
      {/* Eyebrow: THE */}
      <span
        className="font-display font-medium text-white/40"
        style={{
          fontSize: s.eyebrow,
          letterSpacing: '0.5em',
          textIndent: '0.5em',
          marginBottom: isNav ? 4 : 8,
        }}
      >
        THE
      </span>

      {/* Main lockup: TECH REACH — one voice, one colour */}
      <div
        className="font-display font-bold flex items-center ttr-wm-main"
        style={{
          fontSize: s.main,
          letterSpacing: '-0.035em',
          lineHeight: 0.95,
        }}
      >
        <span>TECH</span>
        {sep}
        <span>REACH</span>
      </div>

      {/* Signal hairline — one cyan accent, spans full lockup */}
      <div
        aria-hidden="true"
        className="ttr-wm-hairline"
        style={{
          height: s.bar,
          marginTop: isNav ? 4 : 10,
          width: '100%',
        }}
      />

      {/* Tagline */}
      {showTagline && (
        <span
          className="font-display font-medium text-white/45"
          style={{
            fontSize: s.tag,
            letterSpacing: '0.36em',
            marginTop: isNav ? 5 : 10,
          }}
        >
          TALENT · TECH · SCALE
        </span>
      )}

      <style>{`
        .ttr-wm-main {
          background: linear-gradient(180deg, #FFFFFF 0%, #F5FAFF 50%, #DDEEF6 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .ttr-wm-hairline {
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(103, 232, 249, 0.55) 20%,
            rgba(34, 211, 238, 0.9) 50%,
            rgba(103, 232, 249, 0.55) 80%,
            transparent 100%);
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};

export default Wordmark;
